/**
 * 698 小红书分享活动记录列表页配置
 *
 * 列口径来源：后台 PRD §3.2.1（21 列）
 * 后端接口：POST /api/d1/xhsActivity/admin/page（pageNum 为 **1-based**）
 */

/**
 * 小红书活动状态 —— **后台文案**
 *
 * 注意：后端 XhsNoteStatusEnum.desc 用的是**C 端口径**（如「审核通过（公示中）」「笔记审核中」
 * 「尚未参与」），后台 PRD 要求的是「展示中」「审核中」「未通过」。两者不能混用，
 * 故本页不直接用后端的 statusDesc，一律按 code 走下面这张表。
 */
const statusMap = {
  pending: "待参与",
  pending2: "审核中",
  showing: "展示中",
  showing_waiting: "展示中（待发放）",
  granted: "已发放",
  rejected: "未通过",
};

/**
 * 状态 → Element tag 类型（**与后台原型 XHS_STATUS_CLASS 一致**）：
 *   待参与 info（灰）· 审核中 warning（橙）· 展示中 / 展示中（待发放）默认（蓝）·
 *   未通过 danger（红）· 已发放 success（绿）
 *
 * 原型里「展示中」写的是 `primary`，Element 的 tag 没有 primary 类型 —— 默认不带 type 就是那套蓝色，
 * 故这里用空串表示「默认色」，不要写成 "primary"（el-tag 会原样拼成 el-tag--primary，取不到样式）。
 */
export const statusTagType = {
  pending: "info",
  pending2: "warning",
  showing: "",
  showing_waiting: "",
  rejected: "danger",
  granted: "success",
};

/** 奖励发放状态 → tag 类型（原型：已发放 success，其余 info） */
export function grantStatusTagType(row) {
  return row && row.grantStatus === "SUCCESS" ? "success" : "info";
}

/**
 * 上传图片的**相对路径** → 可访问地址。
 *
 * 后端存的是相对 viewPath（与环境无关），域名由展示侧自己拼——照本仓 giftRecord 的分支写法。
 * 已是 http(s) 绝对地址则原样返回，兼容改口径之前存下的历史数据。
 */
export function fileUrl(p) {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  const host = window.location.hostname;
  const prefix = host.includes("shzhida.com")
    ? "http://ocharge.shzhida.com:6771"
    : "http://192.168.8.174:7997";
  return prefix + "/" + String(p).replace(/^\/+/, "");
}

const pageConfig = {};

pageConfig.listBreadCrumb = [
  { name: "营销活动", path: "" },
  { name: "698小红书分享活动", path: "" },
];

pageConfig.listColumns = [
  // 1
  { label: "活动订单编号", props: "activityOrderNo", minWidth: 200, slot: true },
  // 2
  { label: "用户账号", props: "userNo", minWidth: 140 },
  // 3
  { label: "用户姓名", props: "userName", minWidth: 100 },
  // 4 手机号明文不脱敏（PRD 明确要求）
  { label: "手机号", props: "mobile", minWidth: 130 },
  // 5
  { label: "活动名称", props: "activityName", minWidth: 160 },
  // 6 排序键
  { label: "笔记提交时间", props: "submitTime", minWidth: 165 },
  // 7 点击新窗口直达小红书 + 完整链接折行 + 复制（见 list.vue 的 slot）
  { label: "小红书推文链接", props: "noteUrl", minWidth: 320, slot: true },
  // 8 推文截图：后端给 3 个字段（账号/发布详情/笔记），合并成一列渲染
  { label: "推文截图", props: "screenshots", minWidth: 190, slot: true },
  // 9 状态（后台文案，按 code 映射，不用后端 statusDesc）
  {
    label: "小红书活动状态",
    props: "status",
    minWidth: 150,
    slot: true, // 渲染成带颜色的 el-tag，配色见 statusTagType
  },
  // 10
  { label: "不通过原因", props: "rejectReason", minWidth: 180, tooltip: true },
  // 11
  { label: "审核人", props: "auditUserName", minWidth: 120 },
  // 12
  { label: "审核时间", props: "auditTime", minWidth: 165 },
  // 13 展示期起止：由 showStartTime / showEndTime 合成
  { label: "展示期起止", props: "showPeriod", minWidth: 300, slot: true },
  // 14
  { label: "剩余展示天数", props: "showRemainingDays", minWidth: 120, slot: true },
  // 15
  { label: "预期奖励月数", props: "expectedMonths", minWidth: 115 },
  // 16
  { label: "实际奖励月数", props: "grantMonths", minWidth: 115, slot: true },
  // 17 奖励发放状态（文案由后端 grantStatusDesc 给出，颜色本页按 code 映射）
  {
    label: "奖励发放状态",
    props: "grantStatusDesc",
    minWidth: 130,
    slot: true, // 渲染成 el-tag：已发放 success、其余 info
  },
  // 18 发放后生成的 GIFT 服务费支付流水号（原「服务费奖励记录ID」已更名，PRD §3.5）
  { label: "服务费支付流水号", props: "feeRecordId", minWidth: 200, slot: true },
  // 19
  { label: "发放人", props: "grantUserName", minWidth: 120, slot: true },
  // 20
  { label: "发放时间", props: "grantTime", minWidth: 165, slot: true },
  // 21 操作：按钮状态机由后端派生（canAudit/canGrant/canRetry + operationDisabledReason），
  //    前端只渲染，不自行判断 —— 避免各页各判一套
  { label: "操作", props: "operate", minWidth: 180, fixed: "right", slot: true },
];

pageConfig.tableConfig = {
  showSelection: false,
  showIndex: false,
};

pageConfig.pagination = {
  current: 1,
  total: 0,
  pageSize: 10,
  pageSizes: [10, 20, 50, 100],
};

// 供页面内复用
pageConfig.statusMap = statusMap;

export default pageConfig;
